// ----------------------------------------------------------------------- imports externos
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import TablaUnidades from "../components/tablas/TablaUnidades";
import { multaPorId, multasFurgones, multasTractores } from "../services/multas";
import { buscarFila, useItemPedido } from "../hooks/useItemPedido";
import { ruta as rutaDeUnidad } from "../shared/unidad";
import {
  etiquetaCorta as empresaCorta,
  nombreDe as empresaNombre,
} from "../shared/empresa";
import { hace } from "../shared/ficha";
import Loading from "./Loading";
import Patente from "../components/funcionales/Patente";

/* Multas de tránsito de toda la flota: quiénes tienen, cuánto es y hace
 * cuánto se consultó. Sólo aparecen acá los dominios que alguna vez se
 * consultaron -- no es un padrón, es lo que ya se sabe.
 *
 * Tractores y furgones separados, mismo criterio que RTO: son dos flotas con
 * su propio padrón y su propia columna de empresa, y `multas_tractores()` /
 * `multas_furgones()` ya vienen separadas de la base por el mismo motivo que
 * `rto_tractores()`/`rto_furgones()` (ver RTO.jsx).
 */

const VISTAS = new Set(["tractores", "furgones"]);

/* Patente comparable: sin espacios ni guiones, en mayúscula. */
const patente = (d) => String(d ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

/* «2026-08-14» → «14/08/2026», sin pasar por `Date` (es una fecha sin hora:
 * con zona horaria podría correrse al día anterior). */
const dia = (f) => (f ? String(f).slice(0, 10).split("-").reverse().join("/") : "");

const pesos = (n) =>
  "$" + new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(n || 0);

const columnas = (tipo) => [
  {
    clave: "interno",
    titulo: "Unidad",
    /* El interno es el enlace y la patente va al lado, como chapa Mercosur
     * (Igna, 03/10: «quedan re facheras»). Afuera del enlace y no adentro:
     * una chapa subrayada deja de parecer una chapa. */
    celda: (f) => (
      <>
        <Link to={rutaDeUnidad(tipo, f.interno)}>{f.interno}</Link>
        {f.dominio ? (
          <>
            {" "}
            <Patente dominio={f.dominio} />
          </>
        ) : null}
      </>
    ),
  },
  {
    clave: "empresa",
    titulo: "Empresa",
    valor: (f) => empresaCorta(f.empresa),
    celda: (f) => (
      <span title={empresaNombre(f.empresa)}>{empresaCorta(f.empresa)}</span>
    ),
    filtro: true,
  },
  { clave: "cantidad", titulo: "Actas" },
  {
    clave: "cantidad_impaga",
    titulo: "Impagas",
    clase: (f) => (f.cantidad_impaga > 0 ? "mal" : ""),
  },
  {
    clave: "monto_impago",
    titulo: "Monto impago",
    valor: (f) =>
      "$" + new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(f.monto_impago || 0),
  },
  {
    clave: "discrepancias",
    titulo: "Discrepancias",
    clase: (f) => (f.discrepancias > 0 ? "mal" : "tenue"),
    celda: (f) => (f.discrepancias > 0 ? f.discrepancias : "—"),
  },
  {
    clave: "consultada_en",
    titulo: "Consultado",
    celda: (f) => (f.consultada_en ? hace(f.consultada_en) : "nunca"),
  },
];

const Multas = () => {
  const [params, setParams] = useSearchParams();
  const [vista, setVista] = useState(() =>
    VISTAS.has(params.get("vista")) ? params.get("vista") : "tractores",
  );
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);

  /* EL ACTA QUE PIDIÓ EL BUSCADOR. Igna, 02/10: «que se abra en el ítem
   * exacto, no solo la pantalla». El buscador encuentra ACTAS y esta pantalla
   * lista UNIDADES, así que «abrir» es las dos cosas: el acta se muestra
   * arriba de la tabla (qué, cuándo, cuánto, si está paga) y la fila de su
   * unidad titila, en la flota que corresponda — si no está en Tractores se
   * prueba en Acoplados. Si el acta no vuelve de la base (se borró, o no hay
   * permiso para verla) se dice; si la patente no está en ninguna de las dos
   * flotas, el acta queda a la vista igual. */
  const { ver, resaltar, listo } = useItemPedido();
  const [acta, setActa] = useState(null);
  const [avisoPedido, setAvisoPedido] = useState("");
  const probadas = useRef(new Set());

  useEffect(() => {
    if (!ver) return;
    let vivo = true;
    setActa(null);
    setAvisoPedido("");
    probadas.current = new Set();
    const noEsta = () => {
      if (!vivo) return;
      setAvisoPedido("No encontré esa multa.");
      listo();
    };
    multaPorId(ver)
      .then((m) => (m ? vivo && setActa(m) : noEsta()))
      .catch(noEsta);
    return () => {
      vivo = false;
    };
  }, [ver, listo]);

  useEffect(() => {
    let vivo = true;
    setFilas(null);
    setError(null);
    const traer = vista === "tractores" ? multasTractores : multasFurgones;
    traer()
      .then((r) => vivo && setFilas(r))
      .catch((e) => vivo && setError(e?.message || "No se pudo consultar"));
    return () => {
      vivo = false;
    };
  }, [vista]);

  const cambiarVista = (v) => {
    // `filas` se vacía ACÁ y no sólo en el efecto: si no, el primer dibujo con
    // la vista nueva todavía tendría las filas de la otra flota.
    if (v !== vista) setFilas(null);
    setVista(v);
    setParams((p) => {
      p.set("vista", v);
      return p;
    });
  };

  const filaDelActa =
    acta && (filas || []).find((f) => patente(f.dominio) === patente(acta.dominio));
  const tipo = vista === "tractores" ? "tractor" : "furgon";

  // Con el acta y las filas de una flota: titila su unidad, o se prueba la otra.
  useEffect(() => {
    if (!ver || !acta || !filas || String(acta.id) !== ver) return;
    probadas.current.add(vista);
    if (filaDelActa) {
      resaltar(buscarFila(ver));
      listo();
      return;
    }
    const otra = vista === "tractores" ? "furgones" : "tractores";
    if (!probadas.current.has(otra)) return cambiarVista(otra);
    listo(); // en ninguna de las dos flotas: queda el acta sola
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ver, acta, filas, vista]);

  return (
    <div className="tk tk--ancho">
      <h2>Multas de tránsito</h2>

      <div className="tk-filtros">
        <button
          type="button"
          className={"tk-chip" + (vista === "tractores" ? " activo" : "")}
          onClick={() => cambiarVista("tractores")}
        >
          Tractores
        </button>
        <button
          type="button"
          className={"tk-chip" + (vista === "furgones" ? " activo" : "")}
          onClick={() => cambiarVista("furgones")}
        >
          Acoplados
        </button>
      </div>

      {avisoPedido && (
        <p className="panel-aviso suave" role="status">
          {avisoPedido}
        </p>
      )}
      {acta && (
        <div className="panel-aviso suave" role="status">
          <h3>
            Acta {acta.acta} · <Patente dominio={acta.dominio} />
          </h3>
          <p className="panel-aviso__chico">
            {[dia(acta.ocurrida_en), acta.jurisdiccion, acta.lugar]
              .filter(Boolean)
              .join(" · ")}
            {acta.descripcion ? `. ${acta.descripcion}` : ""}. {pesos(acta.monto)}{" "}
            · {acta.pagada ? "pagada" : "impaga"}
            {acta.discrepancia ? " · con discrepancia" : ""}.{" "}
            {filaDelActa && rutaDeUnidad(tipo, filaDelActa.interno) ? (
              <>
                Para marcarla pagada, la{" "}
                <Link to={rutaDeUnidad(tipo, filaDelActa.interno)}>
                  ficha del {filaDelActa.interno}
                </Link>
                .
              </>
            ) : filas && probadas.current.size === 2 ? (
              "Esa patente no está en el padrón de tractores ni de acoplados."
            ) : null}
          </p>
        </div>
      )}

      {error && <p className="pf-nopude">{error}</p>}
      {!error && !filas && <Loading />}
      {!error && filas && (
        <TablaUnidades
          columnas={columnas(tipo)}
          filas={filas}
          atributosFila={(f) =>
            acta && patente(f.dominio) === patente(acta.dominio)
              ? { "data-ver": acta.id }
              : undefined
          }
          vacio="Ninguna unidad de esta flota tiene una consulta de multas registrada todavía."
        />
      )}
    </div>
  );
};

export default Multas;
